// --------------------------------------------------------------
// Created On: 2023-07-20
// Author: Zachary Thomas
//
// Last Modified: 2026-08-03
// Modified By: Jonathon Hicke
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import useApi from "src/hooks/useApi";
import { POWER_TO_PANEL_ATTRIBUTE, COMMON_ALARM_ATTRIBUTE } from "../../../constants/attributes";
import formatDateShortLocal from "../../../utilities/time/formatDateShortLocal";
import LightIcon from "../../../components/LightIcon/LightIcon";
import isValueBooleanString from "../../../utilities/isValueBooleanString";
import {
  COPILOT_DEVICE_TYPE,
  API,
  COPILOT_PMG_DEVICE_TYPE,
  COPILOT_EDGE_DEVICE_TYPE,
} from "../../../constants/miscellaneous";
import SampleDeviceDataButton from "../../../components/SampleDeviceDataButton/SampleDeviceDataButton";
import useReduxSelector from "../../../hooks/useReduxSelector";
import nowToUtcIso from "../../../utilities/time/nowToUtcIso";
import styles from "./OperationDetailsPanel.module.scss";

// Panel for viewing operation related details about an asset.
export default function OperationDetailsPanel(props: Props): Component {
  const [deviceIsAwaitingCommand, setDeviceIsAwaitingCommand] = useState<boolean | null>(null);
  const [pendingStatusUtc, setPendingStatusUtc] = useState<string | null>(null);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Whenever the dataPollCounter increments, attempt to see if the current device is already processing a command.
  useApi(
    () => {
      return true;
    },
    {
      url: `${API}/company/${currentUser.companyId}/asset/${props.assetId}/operation/pendingcommand`,
      method: "GET",
    },
    async (response: Response, responseBody: PendingCommandResponseBody) => {
      if (response.ok && responseBody) {
        setDeviceIsAwaitingCommand(!responseBody.pendingCommand);
        setPendingStatusUtc(nowToUtcIso(0));
      } else {
        setDeviceIsAwaitingCommand(false);
        setPendingStatusUtc(nowToUtcIso(0));
      }
    },
    [props.dataPollCounter]
  );

  // Determines if a light should be shown as on or off based on the attribute code and value.
  function lightIsOn(attributeCode: string, value: string | null): boolean {
    if (value === null) {
      return false;
    } else if (props.staleAttributeCodes.includes(attributeCode)) {
      return false;
    } else if (attributeCode === POWER_TO_PANEL_ATTRIBUTE) {
      return true;
    } else if (isValueBooleanString(value) && value.toLowerCase() === "true") {
      return true;
    } else {
      return false;
    }
  }

  // Determines what color a light should be based on the attribute code and value.
  function lightColor(attributeCode: string, value: string | null): "red" | "green" {
    if (value === null) {
      return "red";
    } else if (
      attributeCode === POWER_TO_PANEL_ATTRIBUTE &&
      isValueBooleanString(value) &&
      value.toLowerCase() === "false"
    ) {
      return "red";
    } else if (
      attributeCode === COMMON_ALARM_ATTRIBUTE &&
      isValueBooleanString(value) &&
      value.toLowerCase() === "true"
    ) {
      return "red";
    } else {
      return "green";
    }
  }

  return (
    <div data-test="operation-attributes-panel" className={styles.body}>
      <div className={styles.titleBar}>
        <label className={styles.title}>Attributes</label>
        {props.deviceId !== null && props.userCanOperate && (
          <SampleDeviceDataButton
            className="float-end me-3"
            assetId={props.assetId}
            disabled={props.loading}
            onIsSamplingChange={(isSampling) => props.onIsSamplingChange(isSampling)}
            smallButton
          />
        )}
      </div>
      <div>
        <table className={`${styles.table} table mb-0 pb-0`}>
          <thead className={styles.thead}>
            <tr>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>Value</th>
              <th className={styles.th} />
              <th className={styles.th}>Time</th>
            </tr>
          </thead>
          <tbody>
            {props.attributes.map((attribute, i) => (
              <tr
                data-test="operation-attribute-row"
                key={i}
                className={
                  props.staleAttributeCodes.includes(attribute.attributeCode) ? styles.staleData : styles.activeData
                }
              >
                <td data-test="operation-details-attribute-name">{attribute.attributeName}</td>
                <td data-test="operation-details-attribute-value">
                  {attribute.currentValue === null
                    ? "N/A"
                    : `${attribute.currentValue} ${attribute.unitShortName === null ? "" : attribute.unitShortName}`}
                </td>
                <td data-test="operation-details-attribute-light">
                  {isValueBooleanString(attribute.currentValue) && (
                    <LightIcon
                      isOn={lightIsOn(attribute.attributeCode, attribute.currentValue)}
                      color={lightColor(attribute.attributeCode, attribute.currentValue)}
                    />
                  )}
                </td>
                <td data-test="operation-details-attribute-time">
                  {attribute.currentValueUtc === null ? "N/A" : formatDateShortLocal(attribute.currentValueUtc)}
                </td>
              </tr>
            ))}

            {/* Show Co-Pilot's pending command status. */}
            {[COPILOT_DEVICE_TYPE, COPILOT_PMG_DEVICE_TYPE, COPILOT_EDGE_DEVICE_TYPE].includes(
              props.deviceType || ""
            ) &&
              deviceIsAwaitingCommand !== null && (
                <tr data-test="operation-pending-command-row" className={styles.activeData}>
                  <td data-test="operation-pending-command-name">Monitoring Device is Ready</td>
                  <td data-test="operation-pending-command-value">{deviceIsAwaitingCommand ? "True" : "False"}</td>
                  <td data-test="operation-pending-command-light">
                    <LightIcon isOn={true} color={deviceIsAwaitingCommand ? "green" : "red"} />
                  </td>
                  <td data-test="operation-pending-command-time">
                    {pendingStatusUtc === null ? "N/A" : formatDateShortLocal(pendingStatusUtc)}
                  </td>
                </tr>
              )}

            {props.digitalOutputs !== undefined &&
              props.digitalOutputs !== null &&
              props.digitalOutputs.map((output, i) => (
                <tr data-test="operation-digital-output-row" key={i} className={styles.activeData}>
                  <td data-test="operation-digital-output-name">
                    {output.operationLabel}&nbsp;(Last known DO #{output.position})
                  </td>
                  <td data-test="operation-digital-output-value">
                    {output.currentValue !== null && output.currentValue === true
                      ? output.trueLabel
                      : output.falseLabel}
                  </td>
                  <td data-test="operation-digital-output-light"></td>
                  <td data-test="operation-digital-output-time">
                    {output.currentValueUtc === null ? "N/A" : formatDateShortLocal(output.currentValueUtc)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface Props {
  assetId: number;
  attributes: ListAttribute[];
  staleAttributeCodes: string[];
  digitalOutputs: DigitalOutput[];
  deviceId: number | null;
  deviceType: string | null;
  userCanOperate: boolean;
  dataPollCounter: number;
  loading: boolean;
  onIsSamplingChange: (isSampling: boolean) => void;
}

interface ListAttribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  unitShortName: string;
  currentValue: string | null;
  currentValueUtc: string | null;
}

interface DigitalOutput {
  position: number;
  operationLabel: string;
  trueLabel: string;
  falseLabel: string;
  currentValue: boolean | null;
  currentValueUtc: string | null;
}

interface PendingCommandResponseBody {
  pendingCommand: boolean;
}
